import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';
import SecurityQuestionsEditor from '../components/SecurityQuestionsEditor';

const ProfileEdit = ({ user, setUser, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [delegate, setDelegate] = useState(null);
  const [delegateForm, setDelegateForm] = useState({
    delegate_name: '', delegate_email: '', delegate_phone: '', delegate_relationship: '',
    access_keyword: '', access_permissions: ['legal_documents', 'profile_info', 'client_info', 'vitals_history']
  });
  const [showDelegateForm, setShowDelegateForm] = useState(false);

  useEffect(() => {
    const fetchDelegate = async () => {
      try {
        const res = await axios.get(`${API}/user/emergency-delegate`);
        if (res.data.configured) {
          setDelegate(res.data);
          setDelegateForm({
            delegate_name: res.data.delegate_name || '',
            delegate_email: res.data.delegate_email || '',
            delegate_phone: res.data.delegate_phone || '',
            delegate_relationship: res.data.delegate_relationship || '',
            access_keyword: '',
            access_permissions: res.data.access_permissions || []
          });
        }
      } catch {}
    };
    fetchDelegate();
  }, []);

  const handleDelegateSubmit = async () => {
    if (!delegateForm.delegate_name || !delegateForm.delegate_email || !delegateForm.access_keyword) {
      toast.error('Name, email, and keyword are required');
      return;
    }
    try {
      await axios.post(`${API}/user/emergency-delegate`, delegateForm);
      toast.success('Emergency delegate configured');
      setShowDelegateForm(false);
      const res = await axios.get(`${API}/user/emergency-delegate`);
      if (res.data.configured) setDelegate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save delegate');
    }
  };

  const handleRemoveDelegate = async () => {
    if (!window.confirm('Remove emergency delegate? They will no longer be able to access your information.')) return;
    try {
      await axios.delete(`${API}/user/emergency-delegate`);
      setDelegate(null);
      toast.success('Emergency delegate removed');
    } catch { toast.error('Failed to remove delegate'); }
  };

  const [formData, setFormData] = useState({
    name: user.name || '',
    phone: user.phone || '',
    phone_alt1: user.phone_alt1 || '',
    phone_alt2: user.phone_alt2 || '',
    email_alt1: user.email_alt1 || '',
    email_alt2: user.email_alt2 || '',
    county: user.county || '',
    state: user.state || '',
    age: user.age || '',
    medical_conditions: user.medical_conditions || '',
    health_issues: user.health_issues || '',
    client_name: user.client_name || '',
    client_address: user.client_address || '',
    client_county: user.client_county || '',
    client_state: user.client_state || '',
    client_age: user.client_age || '',
    client_health_condition: user.client_health_condition || '',
    client_emergency_contacts: user.client_emergency_contacts || '',
    client_history: user.client_history || '',
    special_instructions: user.special_instructions || '',
    notify_on_successful_checkin: user.notify_on_successful_checkin || false,
    notify_on_missed_checkin: user.notify_on_missed_checkin !== undefined ? user.notify_on_missed_checkin : true,
    include_location_in_notification: user.include_location_in_notification !== undefined ? user.include_location_in_notification : true,
    include_notes_in_notification: user.include_notes_in_notification !== undefined ? user.include_notes_in_notification : true,
    notify_via_sms: user.notify_via_sms || false,
    notify_via_phone: user.notify_via_phone || false,
    notify_via_email: user.notify_via_email !== undefined ? user.notify_via_email : true,
    notify_via_google_home: user.notify_via_google_home || false,
    notify_via_alexa: user.notify_via_alexa || false,
    reminder_time_hours: user.reminder_time_hours || 2,
    enable_911_auto_dial: user.enable_911_auto_dial || false,
    emergency_number: user.emergency_number || '911',
    direct_notification_hours: user.direct_notification_hours || 3,
    emergency_contact_hours: user.emergency_contact_hours || 5,
    call_911_hours: user.call_911_hours || 7,
    social_facebook: user.social_facebook || '',
    social_x: user.social_x || '',
    social_instagram: user.social_instagram || '',
    social_linkedin: user.social_linkedin || '',
    social_youtube: user.social_youtube || '',
    social_tiktok: user.social_tiktok || '',
    social_other: user.social_other || '',
    publicly_listed: user.publicly_listed || false,
    directory_bio: user.directory_bio || '',
    security_q1: user.security_q1 || '',
    security_a1: '',
    security_q2: user.security_q2 || '',
    security_a2: '',
    security_q3: user.security_q3 || '',
    security_a3: ''
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const n of [1, 2, 3]) {
      const q = (formData[`security_q${n}`] || '').trim();
      if (q && q !== (user[`security_q${n}`] || '') && !formData[`security_a${n}`].trim()) {
        toast.error(`Please enter an answer for security question ${n}`);
        return;
      }
    }
    setLoading(true);

    try {
      const res = await axios.put(`${API}/user/profile`, formData);
      
      // Update local user data
      const safeForm = Object.fromEntries(Object.entries(formData).filter(([k]) => !k.startsWith('security_a')));
      const updatedUser = { ...user, ...safeForm, ...res.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully!');
      navigate('/profile');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container" data-testid="profile-edit-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="page-title">Edit Profile</h1>

        <form onSubmit={handleSubmit}>
          {/* Avatar Upload Section */}
          <div className="section-card" data-testid="avatar-section">
            <h2 className="section-title">Profile Pictures</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
              
              {/* Caregiver Avatar */}
              <div className="avatar-upload-section">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}>Your Photo</h3>
                <div className="avatar-preview">
                  {user.caregiver_avatar ? (
                    <img src={user.caregiver_avatar} alt="Your profile" />
                  ) : (
                    <span>👤</span>
                  )}
                </div>
                <input
                  type="file"
                  id="caregiver-avatar"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload/avatar/caregiver`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const updatedUser = { ...user, caregiver_avatar: response.data.avatar };
                        setUser(updatedUser);
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                        toast.success('Profile picture updated!');
                      } catch (error) {
                        toast.error('Upload failed');
                      }
                    }
                  }}
                  data-testid="caregiver-avatar-input"
                />
                <label htmlFor="caregiver-avatar" className="avatar-upload-btn">
                  📸 Upload Your Photo
                </label>
              </div>

              {/* Client Avatar */}
              <div className="avatar-upload-section">
                <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}>Client's Photo</h3>
                <div className="avatar-preview">
                  {user.client_avatar ? (
                    <img src={user.client_avatar} alt="Client profile" />
                  ) : (
                    <span>👥</span>
                  )}
                </div>
                <input
                  type="file"
                  id="client-avatar"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload/avatar/client`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const updatedUser = { ...user, client_avatar: response.data.avatar };
                        setUser(updatedUser);
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                        toast.success('Client photo updated!');
                      } catch (error) {
                        toast.error('Upload failed');
                      }
                    }
                  }}
                  data-testid="client-avatar-input"
                />
                <label htmlFor="client-avatar" className="avatar-upload-btn">
                  📸 Upload Client Photo
                </label>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="section-card" data-testid="personal-info-section">
            <h2 className="section-title">Personal Information</h2>
            
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="name-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="phone">Primary Phone Number</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_alt1">Alternate Phone 1</label>
                <input
                  id="phone_alt1"
                  type="tel"
                  name="phone_alt1"
                  value={formData.phone_alt1}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  data-testid="phone-alt1-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_alt2">Alternate Phone 2</label>
                <input
                  id="phone_alt2"
                  type="tel"
                  name="phone_alt2"
                  value={formData.phone_alt2}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  data-testid="phone-alt2-input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="email_alt1">Alternate Email 1</label>
                <input
                  id="email_alt1"
                  type="email"
                  name="email_alt1"
                  value={formData.email_alt1}
                  onChange={handleChange}
                  placeholder="alternate@example.com"
                  data-testid="email-alt1-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email_alt2">Alternate Email 2</label>
                <input
                  id="email_alt2"
                  type="email"
                  name="email_alt2"
                  value={formData.email_alt2}
                  onChange={handleChange}
                  placeholder="alternate2@example.com"
                  data-testid="email-alt2-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="age">Age</label>
                <input
                  id="age"
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="18"
                  max="120"
                  placeholder="Your age"
                  data-testid="age-input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="county">County</label>
                <input
                  id="county"
                  type="text"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  placeholder="e.g., Gwinnett County"
                  data-testid="county-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  id="state"
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="e.g., Georgia"
                  data-testid="state-input"
                />
              </div>
            </div>
            <p style={{ color: '#6b8899', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              📍 Your location is used to show personalized local resources on the Resources page
            </p>
          </div>

          {/* Client/Patient Information */}
          <div className="section-card" data-testid="client-section">
            <h2 className="section-title">Client/Patient Information (Person with Dementia/Alzheimer's)</h2>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.95rem' }}>
              Critical information about the person you're caring for - shared with emergency contacts if you miss check-ins
            </p>
            
            <div className="form-group">
              <label htmlFor="client_name">Client/Patient Name</label>
              <input
                id="client_name"
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="Full name of person you're caring for"
                data-testid="client-name-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_address">Client/Patient Address</label>
              <textarea
                id="client_address"
                name="client_address"
                rows="2"
                value={formData.client_address}
                onChange={handleChange}
                placeholder="Full address where client lives (for emergency responders)"
                data-testid="client-address-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="client_county">Client County</label>
                <input
                  id="client_county"
                  type="text"
                  name="client_county"
                  value={formData.client_county}
                  onChange={handleChange}
                  placeholder="e.g., Gwinnett County"
                  data-testid="client-county-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="client_state">Client State</label>
                <input
                  id="client_state"
                  type="text"
                  name="client_state"
                  value={formData.client_state}
                  onChange={handleChange}
                  placeholder="e.g., Georgia"
                  data-testid="client-state-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="client_age">Client/Patient Age</label>
                <input
                  id="client_age"
                  type="number"
                  name="client_age"
                  value={formData.client_age}
                  onChange={handleChange}
                  min="1"
                  max="120"
                  placeholder="Age of person you're caring for"
                  data-testid="client-age-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="client_health_condition">Client Health Condition/Diagnosis</label>
              <textarea
                id="client_health_condition"
                name="client_health_condition"
                rows="3"
                value={formData.client_health_condition}
                onChange={handleChange}
                placeholder="Alzheimer's stage, dementia type, other conditions, medications, allergies..."
                data-testid="client-health-condition-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_emergency_contacts">Client's Emergency Contacts</label>
              <textarea
                id="client_emergency_contacts"
                name="client_emergency_contacts"
                rows="3"
                value={formData.client_emergency_contacts}
                onChange={handleChange}
                placeholder="Family members, doctors, or other contacts for the client/patient&#10;Example: Son John - 555-1234, Dr. Smith - 555-5678"
                data-testid="client-emergency-contacts-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_history">Client Medical History & Care Notes</label>
              <textarea
                id="client_history"
                name="client_history"
                rows="4"
                value={formData.client_history}
                onChange={handleChange}
                placeholder="Diagnosis date, progression, behavioral patterns, triggers, daily routine, special needs..."
                data-testid="client-history-input"
              />
            </div>
          </div>

          {/* Medical & Health Information */}
          <div className="section-card" data-testid="health-section">
            <h2 className="section-title">Your Medical & Health Information</h2>
            
            <div className="form-group">
              <label htmlFor="medical_conditions">Your Medical Conditions</label>
              <textarea
                id="medical_conditions"
                name="medical_conditions"
                rows="3"
                value={formData.medical_conditions}
                onChange={handleChange}
                placeholder="Diabetes, heart condition, allergies, etc."
                data-testid="medical-conditions-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="health_issues">Current Health Issues</label>
              <textarea
                id="health_issues"
                name="health_issues"
                rows="3"
                value={formData.health_issues}
                onChange={handleChange}
                placeholder="Any current health concerns or recent issues..."
                data-testid="health-issues-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="special_instructions">Special Instructions</label>
              <textarea
                id="special_instructions"
                name="special_instructions"
                rows="4"
                value={formData.special_instructions}
                onChange={handleChange}
                placeholder="Critical information for emergency contacts: medications, location of medical records, doctor contacts, special needs of your client..."
                data-testid="special-instructions-input"
              />
              <small style={{ color: '#d9534f', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block', fontWeight: 600 }}>
                ⚠️ This information will be shared with emergency contacts in case you miss check-ins
              </small>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="section-card" data-testid="notification-section">
            <h2 className="section-title">Notification Preferences</h2>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Configure how and when you want to be notified for missed check-ins
            </p>
            
            <h3 style={{ color: '#2c5364', marginBottom: '1rem', fontSize: '1.1rem' }}>
              ⏰ Escalation Timeline
            </h3>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Set how many hours after a missed check-in each escalation stage triggers
            </p>

            <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
              <div className="form-group">
                <label htmlFor="direct_notification_hours">
                  📱 Stage 1: Notify Me
                </label>
                <select
                  id="direct_notification_hours"
                  name="direct_notification_hours"
                  value={formData.direct_notification_hours}
                  onChange={handleChange}
                  data-testid="direct-notification-hours"
                >
                  <option value="1">1 hour</option>
                  <option value="2">2 hours</option>
                  <option value="3">3 hours</option>
                  <option value="4">4 hours</option>
                  <option value="6">6 hours</option>
                  <option value="12">12 hours</option>
                </select>
                <small style={{ color: '#6b8899', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Send SMS, calls, emails to me
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="emergency_contact_hours">
                  👥 Stage 2: Notify Contacts
                </label>
                <select
                  id="emergency_contact_hours"
                  name="emergency_contact_hours"
                  value={formData.emergency_contact_hours}
                  onChange={handleChange}
                  data-testid="emergency-contact-hours"
                >
                  <option value="2">2 hours</option>
                  <option value="3">3 hours</option>
                  <option value="4">4 hours</option>
                  <option value="5">5 hours</option>
                  <option value="6">6 hours</option>
                  <option value="12">12 hours</option>
                  <option value="24">24 hours</option>
                </select>
                <small style={{ color: '#6b8899', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Alert emergency contacts
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="call_911_hours">
                  🚨 Stage 3: Call 911
                </label>
                <select
                  id="call_911_hours"
                  name="call_911_hours"
                  value={formData.call_911_hours}
                  onChange={handleChange}
                  data-testid="call-911-hours"
                >
                  <option value="4">4 hours</option>
                  <option value="5">5 hours</option>
                  <option value="6">6 hours</option>
                  <option value="7">7 hours</option>
                  <option value="8">8 hours</option>
                  <option value="12">12 hours</option>
                  <option value="24">24 hours</option>
                </select>
                <small style={{ color: '#6b8899', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Auto-dial emergency services
                </small>
              </div>
            </div>

            <div style={{ background: '#e7f3ff', padding: '1rem', borderRadius: '8px', marginBottom: '2rem' }}>
              <strong>📋 Your Escalation Timeline:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                <li><strong>Hour 0:</strong> Check-in missed</li>
                <li><strong>Hour {formData.direct_notification_hours}:</strong> You receive SMS, calls, emails, speaker announcements</li>
                <li><strong>Hour {formData.emergency_contact_hours}:</strong> Emergency contacts are alerted via email</li>
                {formData.enable_911_auto_dial && (
                  <li style={{ color: '#d9534f' }}><strong>Hour {formData.call_911_hours}:</strong> 🚨 911 auto-dial activated</li>
                )}
              </ul>
            </div>

            <h3 style={{ color: '#2c5364', marginBottom: '1rem', fontSize: '1.1rem' }}>
              Notification Channels
            </h3>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Choose how you want to be reminded when you miss a check-in
            </p>
            
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_via_email"
                  name="notify_via_email"
                  checked={formData.notify_via_email}
                  onChange={handleChange}
                  data-testid="notify-email-checkbox"
                />
                <div>
                  <label htmlFor="notify_via_email">
                    <strong>📧 Email Notifications</strong>
                  </label>
                  <div className="checkbox-description">
                    Receive reminder emails when you miss a check-in (Recommended)
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_via_sms"
                  name="notify_via_sms"
                  checked={formData.notify_via_sms}
                  onChange={handleChange}
                  data-testid="notify-sms-checkbox"
                />
                <div>
                  <label htmlFor="notify_via_sms">
                    <strong>📱 SMS Text Messages</strong>
                  </label>
                  <div className="checkbox-description">
                    Receive text messages for missed check-ins (requires Twilio setup)
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_via_phone"
                  name="notify_via_phone"
                  checked={formData.notify_via_phone}
                  onChange={handleChange}
                  data-testid="notify-phone-checkbox"
                />
                <div>
                  <label htmlFor="notify_via_phone">
                    <strong>📞 Automated Phone Calls</strong>
                  </label>
                  <div className="checkbox-description">
                    Receive voice calls with text-to-speech reminders (requires Twilio setup)
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_via_google_home"
                  name="notify_via_google_home"
                  checked={formData.notify_via_google_home}
                  onChange={handleChange}
                  data-testid="notify-google-home-checkbox"
                />
                <div>
                  <label htmlFor="notify_via_google_home">
                    <strong>🏠 Google Home Announcements</strong>
                  </label>
                  <div className="checkbox-description">
                    Hear reminders on your Google Home devices (simulation mode - coming soon)
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_via_alexa"
                  name="notify_via_alexa"
                  checked={formData.notify_via_alexa}
                  onChange={handleChange}
                  data-testid="notify-alexa-checkbox"
                />
                <div>
                  <label htmlFor="notify_via_alexa">
                    <strong>🔊 Alexa Announcements</strong>
                  </label>
                  <div className="checkbox-description">
                    Hear reminders on your Alexa devices (simulation mode - coming soon)
                  </div>
                </div>
              </div>
            </div>

            <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid #e8f4f8' }} />

            <h3 style={{ color: '#2c5364', marginBottom: '1rem', fontSize: '1.1rem' }}>
              Emergency Contact Notifications
            </h3>

            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_on_successful_checkin"
                  name="notify_on_successful_checkin"
                  checked={formData.notify_on_successful_checkin}
                  onChange={handleChange}
                  data-testid="notify-success-checkbox"
                />
                <div>
                  <label htmlFor="notify_on_successful_checkin">
                    <strong>Notify Contacts on Successful Check-in</strong>
                  </label>
                  <div className="checkbox-description">
                    Send email to emergency contacts every time you successfully check in
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="notify_on_missed_checkin"
                  name="notify_on_missed_checkin"
                  checked={formData.notify_on_missed_checkin}
                  onChange={handleChange}
                  data-testid="notify-missed-checkbox"
                />
                <div>
                  <label htmlFor="notify_on_missed_checkin">
                    <strong>Notify Contacts on Missed Check-in</strong>
                  </label>
                  <div className="checkbox-description">
                    Alert emergency contacts if you miss your scheduled check-in (Recommended)
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="include_location_in_notification"
                  name="include_location_in_notification"
                  checked={formData.include_location_in_notification}
                  onChange={handleChange}
                  data-testid="include-location-checkbox"
                />
                <div>
                  <label htmlFor="include_location_in_notification">
                    <strong>Include Location in Notifications</strong>
                  </label>
                  <div className="checkbox-description">
                    Share your GPS location with emergency contacts in notifications
                  </div>
                </div>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="include_notes_in_notification"
                  name="include_notes_in_notification"
                  checked={formData.include_notes_in_notification}
                  onChange={handleChange}
                  data-testid="include-notes-checkbox"
                />
                <div>
                  <label htmlFor="include_notes_in_notification">
                    <strong>Include Daily Notes in Notifications</strong>
                  </label>
                  <div className="checkbox-description">
                    Share your daily notes, feelings, and health concerns in notifications
                  </div>
                </div>
              </div>
            </div>

            <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid #e8f4f8' }} />

            <h3 style={{ color: '#d9534f', marginBottom: '1rem', fontSize: '1.1rem' }}>
              🚨 Critical Escalation Settings
            </h3>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Last-resort options when all other notifications fail
            </p>

            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="enable_911_auto_dial"
                  name="enable_911_auto_dial"
                  checked={formData.enable_911_auto_dial}
                  onChange={handleChange}
                  data-testid="enable-911-checkbox"
                />
                <div>
                  <label htmlFor="enable_911_auto_dial">
                    <strong style={{ color: '#d9534f' }}>🚨 Enable Auto-Dial 911/Emergency Services</strong>
                  </label>
                  <div className="checkbox-description">
                    If you don't respond after 2x the reminder time, automatically call emergency services for wellness check
                  </div>
                </div>
              </div>
            </div>

            {formData.enable_911_auto_dial && (
              <div className="form-group" style={{ marginTop: '1rem', background: '#fff3cd', padding: '1rem', borderRadius: '8px' }}>
                <label htmlFor="emergency_number">Emergency Services Number</label>
                <input
                  id="emergency_number"
                  type="text"
                  name="emergency_number"
                  value={formData.emergency_number}
                  onChange={handleChange}
                  placeholder="911 (US), 999 (UK), 112 (EU), etc."
                  data-testid="emergency-number-input"
                />
                <small style={{ color: '#856404', fontSize: '0.875rem', marginTop: '0.5rem', display: 'block' }}>
                  ⚠️ This will place an automated call to emergency services with your location and client information.
                  Only enable if you understand the implications.
                </small>
              </div>
            )}
          </div>

          {/* Social Media Section */}
          <div className="section-card" style={{ marginTop: '2rem' }}>
            <h2 className="section-title" data-testid="social-section-title">
              <i className="fa-solid fa-share-nodes" style={{ marginRight: '8px' }}></i>
              Social Media
            </h2>
            <p style={{ color: '#6b8899', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
              Add your social media profiles to share with emergency contacts and stay connected.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="social_facebook"><i className="fa-brands fa-facebook" style={{ color: '#1877f2', marginRight: '6px' }}></i> Facebook</label>
                <input id="social_facebook" name="social_facebook" type="url" value={formData.social_facebook} onChange={handleChange} placeholder="https://facebook.com/yourprofile" data-testid="social-facebook-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_x"><i className="fa-brands fa-x-twitter" style={{ marginRight: '6px' }}></i> X (Twitter)</label>
                <input id="social_x" name="social_x" type="url" value={formData.social_x} onChange={handleChange} placeholder="https://x.com/yourhandle" data-testid="social-x-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_instagram"><i className="fa-brands fa-instagram" style={{ color: '#e4405f', marginRight: '6px' }}></i> Instagram</label>
                <input id="social_instagram" name="social_instagram" type="url" value={formData.social_instagram} onChange={handleChange} placeholder="https://instagram.com/yourprofile" data-testid="social-instagram-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_linkedin"><i className="fa-brands fa-linkedin" style={{ color: '#0a66c2', marginRight: '6px' }}></i> LinkedIn</label>
                <input id="social_linkedin" name="social_linkedin" type="url" value={formData.social_linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/yourprofile" data-testid="social-linkedin-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_youtube"><i className="fa-brands fa-youtube" style={{ color: '#ff0000', marginRight: '6px' }}></i> YouTube</label>
                <input id="social_youtube" name="social_youtube" type="url" value={formData.social_youtube} onChange={handleChange} placeholder="https://youtube.com/@yourchannel" data-testid="social-youtube-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_tiktok"><i className="fa-brands fa-tiktok" style={{ marginRight: '6px' }}></i> TikTok</label>
                <input id="social_tiktok" name="social_tiktok" type="url" value={formData.social_tiktok} onChange={handleChange} placeholder="https://tiktok.com/@yourprofile" data-testid="social-tiktok-input" />
              </div>
              <div className="form-group">
                <label htmlFor="social_other"><i className="fa-solid fa-link" style={{ marginRight: '6px' }}></i> Other</label>
                <input id="social_other" name="social_other" type="url" value={formData.social_other} onChange={handleChange} placeholder="https://yourwebsite.com" data-testid="social-other-input" />
              </div>
            </div>
          </div>

          {/* Caregiver Directory Section */}
          <div className="section-card" style={{ marginTop: '2rem' }}>
            <h2 className="section-title" data-testid="directory-section-title">
              <i className="fa-solid fa-people-group" style={{ marginRight: '8px', color: '#2c5364' }}></i>
              Caregiver Directory
            </h2>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Connect with other caregivers in your county/state. Your name, location, and a short bio (no address or sensitive details) will be visible to other CareGuard caregivers who also opt in.
            </p>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="publicly_listed"
                name="publicly_listed"
                type="checkbox"
                checked={formData.publicly_listed}
                onChange={handleChange}
                data-testid="publicly-listed-toggle"
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <label htmlFor="publicly_listed" style={{ cursor: 'pointer', fontWeight: 600 }}>
                List me in the Caregiver Directory
              </label>
            </div>
            {formData.publicly_listed && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label htmlFor="directory_bio">Short Bio <span style={{ color: '#94a3b8', fontWeight: 400 }}>(max 250 chars, optional)</span></label>
                <textarea
                  id="directory_bio"
                  name="directory_bio"
                  value={formData.directory_bio}
                  onChange={handleChange}
                  maxLength={250}
                  rows={3}
                  placeholder="e.g., Caring for my mother (early-stage Alzheimer's). Happy to swap tips on routine and respite."
                  data-testid="directory-bio-input"
                />
              </div>
            )}
          </div>

          {/* Security Questions Section */}
          <div className="section-card" style={{ marginTop: '2rem' }} data-testid="security-questions-section">
            <h2 className="section-title">
              <i className="fa-solid fa-shield-halved" style={{ marginRight: '8px', color: '#2c5364' }}></i>
              Security Questions
            </h2>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Used to verify it's you if you forget your password. Pick from the list or write your own question. Leave an answer blank to keep your current one.
            </p>
            <SecurityQuestionsEditor
              values={formData}
              onChange={(name, value) => setFormData((prev) => ({ ...prev, [name]: value }))}
              answerPlaceholder="Answer (leave blank to keep current)"
            />
          </div>

          {/* Emergency Delegate Section */}
          <div className="section-card" style={{ marginTop: '2rem', border: '2px solid #fca5a5' }}>
            <h2 className="section-title" style={{ color: '#dc2626' }} data-testid="delegate-section-title">
              <i className="fa-solid fa-user-shield" style={{ marginRight: '8px' }}></i>
              Emergency Delegate Access
            </h2>
            <p style={{ color: '#6b8899', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Designate a trusted person who can access your critical information if you become incapacitated. They will need your email and a secret keyword you create.
            </p>

            {delegate && !showDelegateForm ? (
              <div data-testid="delegate-info">
                <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', borderLeft: '4px solid #16a34a', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ color: '#2c5364' }}>{delegate.delegate_name}</strong>
                      <div style={{ fontSize: '0.85rem', color: '#6b8899' }}>{delegate.delegate_email} | {delegate.delegate_relationship}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b8899', marginTop: '0.25rem' }}>
                        Access: {delegate.access_permissions?.map(p => p.replace(/_/g, ' ')).join(', ')}
                      </div>
                    </div>
                    <span style={{ padding: '3px 10px', borderRadius: '6px', background: '#dcfce7', color: '#16a34a', fontSize: '0.75rem', fontWeight: '700' }}>Active</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setShowDelegateForm(true)}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #2c5364', background: '#fff', cursor: 'pointer', color: '#2c5364', fontSize: '0.85rem', fontWeight: '600' }}
                    data-testid="edit-delegate-btn">
                    <i className="fa-solid fa-pen"></i> Edit
                  </button>
                  <button type="button" onClick={handleRemoveDelegate}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #ef4444', background: '#fff', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem', fontWeight: '600' }}
                    data-testid="remove-delegate-btn">
                    <i className="fa-solid fa-trash"></i> Remove
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!showDelegateForm && !delegate && (
                  <button type="button" onClick={() => setShowDelegateForm(true)} className="submit-button" style={{ width: 'auto' }} data-testid="add-delegate-btn">
                    <i className="fa-solid fa-plus"></i> Set Up Emergency Delegate
                  </button>
                )}
                {showDelegateForm && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }} data-testid="delegate-form">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Delegate Name *</label>
                        <input value={delegateForm.delegate_name} onChange={e => setDelegateForm({ ...delegateForm, delegate_name: e.target.value })} required data-testid="delegate-name-input" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Delegate Email *</label>
                        <input type="email" value={delegateForm.delegate_email} onChange={e => setDelegateForm({ ...delegateForm, delegate_email: e.target.value })} required data-testid="delegate-email-input" />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Phone</label>
                        <input type="tel" value={delegateForm.delegate_phone} onChange={e => setDelegateForm({ ...delegateForm, delegate_phone: e.target.value })} data-testid="delegate-phone-input" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Relationship *</label>
                        <select value={delegateForm.delegate_relationship} onChange={e => setDelegateForm({ ...delegateForm, delegate_relationship: e.target.value })} data-testid="delegate-relationship-select">
                          <option value="">Select...</option>
                          <option value="spouse">Spouse</option>
                          <option value="child">Child</option>
                          <option value="parent">Parent</option>
                          <option value="sibling">Sibling</option>
                          <option value="attorney">Attorney</option>
                          <option value="friend">Friend</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Secret Access Keyword * <span style={{ fontSize: '0.8rem', color: '#6b8899' }}>(share this with your delegate)</span></label>
                      <input type="text" value={delegateForm.access_keyword} onChange={e => setDelegateForm({ ...delegateForm, access_keyword: e.target.value })}
                        placeholder="Create a secret keyword (min 4 characters)" required data-testid="delegate-keyword-input" />
                    </div>
                    <div style={{ marginTop: '0.25rem' }}>
                      <label style={{ fontWeight: '600', color: '#2c5364', marginBottom: '0.5rem', display: 'block' }}>Access Permissions</label>
                      {[
                        { id: 'legal_documents', label: 'Legal Documents (wills, directives, trusts)', icon: 'fa-file-lines' },
                        { id: 'profile_info', label: 'Profile Information (address, medical, contacts)', icon: 'fa-user' },
                        { id: 'client_info', label: 'Client Information', icon: 'fa-user-group' },
                        { id: 'vitals_history', label: 'Vitals History', icon: 'fa-heart-pulse' }
                      ].map(perm => (
                        <label key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', cursor: 'pointer' }}>
                          <input type="checkbox" checked={delegateForm.access_permissions.includes(perm.id)}
                            onChange={e => {
                              const perms = e.target.checked
                                ? [...delegateForm.access_permissions, perm.id]
                                : delegateForm.access_permissions.filter(p => p !== perm.id);
                              setDelegateForm({ ...delegateForm, access_permissions: perms });
                            }}
                            data-testid={`delegate-perm-${perm.id}`} />
                          <i className={`fa-solid ${perm.icon}`} style={{ color: '#2c5364', width: '16px' }}></i>
                          <span style={{ fontSize: '0.9rem' }}>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button type="button" onClick={handleDelegateSubmit} className="submit-button" style={{ width: 'auto' }} data-testid="save-delegate-btn">
                        <i className="fa-solid fa-shield-halved"></i> Save Delegate
                      </button>
                      <button type="button" onClick={() => setShowDelegateForm(false)}
                        style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#6b8899' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
              data-testid="save-profile-button"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              type="button"
              className="submit-button"
              onClick={() => navigate('/profile')}
              style={{ background: '#6c757d' }}
              data-testid="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileEdit;
